import * as Y from 'yjs';
import { z } from 'zod';
import { readYjsValue } from '../../../lib/diagram/yjsCompat';
import { extractSceneFromYDoc } from '../../orgchart/services/DiagramForkService';
import { generateId } from '../../../utils/idGenerator';
import { base64ToUint8Array } from '../../../utils/yjs';
import { AccessControlService } from '../../access/services/AccessControlService';
import type { RoleLevel } from '../../access/services/AccessControlService';
import type { FieldSensitivity } from '../../../core/fields/types/FieldManifest';
import { KNOWN_PROTECTED_FIELD_KEYS } from '../../../core/fields/security';
import {
  buildAllowedAttrKeys,
  buildRedactedAttrKeys,
  filterNodeAttrs,
  type FieldVisibilityConfig,
} from '../utils/fieldVisibility';
import {
  evaluateRecordVisibility,
  type ViewerTierCeiling,
} from '../../data-binding/services/RecordVisibilityPolicy';
import {
  isPublicViewerEdge,
  PUBLIC_VIEWER_FIELD_VISIBILITY,
} from '../utils/publicViewerPolicy';
import type {
  FieldVisibilityPayload,
  ViewerDomain,
  ViewerEdge,
  ViewerNode,
  ViewerSnapshotPayload,
  ViewerSnapshotRecord,
} from '../models/ViewerSnapshot';
import type { IViewerSnapshotRepository } from '../repositories/IViewerSnapshotRepository';

const OrgChartPublishSchema = z.object({
  id: z.string().optional(),
  title: z.string().max(200).optional(),
  sourceDiagramId: z.string().min(1).max(120).optional(),
  nodes: z.array(z.object({
    id: z.string().min(1).max(120),
    x: z.number(),
    y: z.number(),
    name: z.string().max(200).optional(),
    title: z.string().max(200).optional(),
    department: z.string().max(200).optional(),
    color: z.string().max(64).optional(),
  })).max(10000),
  edges: z.array(z.object({
    from: z.string().min(1).max(120),
    to: z.string().min(1).max(120),
    type: z.string().max(80).optional(),
  })).max(20000),
  fieldVisibility: z.object({
    sensitivityLevel: z.string().max(40),
    includeSections: z.array(z.string().max(60)).max(20).optional(),
    excludeSections: z.array(z.string().max(60)).max(20).optional(),
  }).optional(),
  scene: z.object({
    diagramType: z.string().max(40).optional(),
    nodes: z.array(z.unknown()).max(200000),
    edges: z.array(z.unknown()).max(200000),
    frames: z.array(z.unknown()).max(200000).optional(),
    viewRules: z.unknown().optional(),
    scenarios: z.array(z.unknown()).max(1000).optional(),
    settings: z.unknown().optional(),
  }).optional(),
});

const EMBEDDABLE_VIEWER_DOMAINS = new Set<ViewerDomain>(['orgchart', 'process', 'genogram']);

export class PublishFromDiagramError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number,
  ) {
    super(message);
  }
}

export interface PublishFromDiagramParams {
  shareId: string;
  diagramId: string;
  fieldVisibility?: {
    sensitivityLevel: FieldSensitivity;
    includeSections?: string[];
    excludeSections?: string[];
  };
  // Drizzle DB instance — typed as `unknown` at this boundary because the
  // codebase initializes it dynamically (see worker/index.ts initRequestDb).
  db: unknown;
  auth: { userId?: string | null; orgId?: string };
}

export interface PublishFromDiagramResult {
  version: string;
  publishedAt: string;
  nodeCount: number;
  edgeCount: number;
}

export type R2SnapshotLoader = (snapshotKey: string) => Promise<Uint8Array>;

export interface ViewerSnapshotServiceDependencies {
  loadR2Snapshot?: R2SnapshotLoader;
}

interface StoredDiagramSnapshot {
  ydocSnapshot: string | null;
  snapshotKey: string | null;
  storageProvider: string | null;
}

export interface ValidatePublishAccessParams {
  shareId: string;
  diagramId: string;
  db: unknown;
  auth: { userId?: string | null; orgId?: string };
}

export interface ValidatePublicReadAccessParams {
  shareId: string;
  db: unknown;
}

/** Cap full-scene publishing to keep snapshot payloads bounded. */
const MAX_SCENE_NODES = 2000;

const VIEWER_PRIVATE_CONTAINER_KEYS = new Set([
  'dataBinding',
  'cachedRecord',
  'localChanges',
  'boundRecordState',
]);

export const ORG_NODE_TYPES = new Set([
  'org_position',
  'org_person',
  'org_person_photo',
  'org_person_photo_left',
  'org_person_photo_right',
  'org_person_photo_inner_left',
  'org_person_text',
  'org_department',
]);

export const PROCESS_NODE_TYPES = new Set([
  'process', 'decision', 'start-end', 'document', 'database', 'manual',
  'input-output', 'predefined', 'display', 'storage', 'internal',
  'off-page-connector', 'on-page-connector', 'junction', 'merge', 'or',
  'sort', 'collate', 'extract', 'delay', 'loop-limit', 'preparation',
  'multi-doc', 'manual-operation', 'flowchart-shape',
  'bpmn-event', 'bpmn-gateway', 'bpmn-activity', 'bpmn-data',
  'bpmn-data-store', 'bpmn-annotation', 'bpmn-shape',
]);

export const GENOGRAM_NODE_TYPES = new Set([
  'person', 'genogram-person', 'genogram_person',
]);

export function truncate(value: unknown, max: number): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed.slice(0, max);
}

export function pickRecordValue(record: unknown, keys: string[]): string | undefined {
  if (!record || typeof record !== 'object') return undefined;
  const source = record as Record<string, unknown>;
  for (const key of keys) {
    const value = truncate(source[key], 200);
    if (value) return value;
  }
  return undefined;
}

interface ViewerRedactionPolicy {
  allowedKeys: Set<string>;
  redactedKeys: Set<string>;
  recordTierCeiling: ViewerTierCeiling;
}

interface RecordVisibilityIndex {
  hiddenRecordIds: Set<string>;
  knownRecordIds: Set<string>;
}

interface SanitizedSceneResult {
  scene: ViewerSnapshotPayload['scene'] | undefined;
  hiddenNodeIds: Set<string>;
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function buildViewerRedactionPolicy(): ViewerRedactionPolicy {
  const config: FieldVisibilityConfig = PUBLIC_VIEWER_FIELD_VISIBILITY;
  const allowedKeys = buildAllowedAttrKeys(config);
  const redactedKeys = buildRedactedAttrKeys(config);

  for (const key of KNOWN_PROTECTED_FIELD_KEYS) {
    if (!allowedKeys.has(key)) redactedKeys.add(key);
  }

  return {
    allowedKeys,
    redactedKeys,
    recordTierCeiling: config.sensitivityLevel,
  };
}

function filterViewerFieldRecord(
  record: Record<string, unknown>,
  policy: ViewerRedactionPolicy,
  publicPresentationKeys: ReadonlySet<string> = new Set(),
): Record<string, unknown> {
  const filtered = filterNodeAttrs(record, policy.allowedKeys);

  for (const key of publicPresentationKeys) {
    const value = sanitizeViewerFieldValue(record[key]);
    if (value !== undefined) filtered[key] = value;
  }

  for (const key of Object.keys(filtered)) {
    if (policy.redactedKeys.has(key) || VIEWER_PRIVATE_CONTAINER_KEYS.has(key)) {
      delete filtered[key];
      continue;
    }
    // Public scene DTO fields are scalar presentation values. Do not let an
    // allowlisted field name smuggle an unclassified object subtree through
    // the anonymous snapshot boundary.
    const safeValue = sanitizeViewerFieldValue(filtered[key]);
    if (safeValue === undefined) delete filtered[key];
    else filtered[key] = safeValue;
  }

  if (isObjectRecord(record.customFields)) {
    const customFields = filterViewerFieldRecord(record.customFields, policy);
    if (Object.keys(customFields).length > 0) {
      filtered.customFields = customFields;
    } else {
      delete filtered.customFields;
    }
  }

  return filtered;
}

function sanitizeViewerFieldValue(value: unknown): unknown | undefined {
  if (typeof value === 'string' || typeof value === 'boolean') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (Array.isArray(value) && value.length <= 1_000) {
    const entries = value.filter((entry) =>
      typeof entry === 'string'
      || typeof entry === 'boolean'
      || (typeof entry === 'number' && Number.isFinite(entry)));
    return entries.length === value.length ? entries : undefined;
  }
  return undefined;
}

function sanitizeViewerArrow(value: unknown): { type: string; size?: number } | undefined {
  if (!isObjectRecord(value) || typeof value.type !== 'string' || !PUBLIC_ARROW_TYPES.has(value.type)) {
    return undefined;
  }
  const size = value.size;
  return typeof size === 'number' && Number.isFinite(size) && size >= 0 && size <= 100
    ? { type: value.type, size }
    : { type: value.type };
}

function filterGenogramViewerFieldRecord(
  record: Record<string, unknown>,
  policy: ViewerRedactionPolicy,
  publicPresentationKeys: ReadonlySet<string>,
): Record<string, unknown> {
  const filtered = filterViewerFieldRecord(record, policy, publicPresentationKeys);
  for (const key of ['startArrow', 'endArrow'] as const) {
    const arrow = sanitizeViewerArrow(record[key]);
    if (arrow) filtered[key] = arrow;
  }
  return filtered;
}

const PUBLIC_SCENE_NODE_KEYS = new Set([
  'id', 'type', 'version', 'name', 'sex', 'deceased', 'birthYear', 'deathYear',
  'location', 'fillColor', 'strokeColor', 'strokeWidth', 'text', 'fontSize',
  'fontFamily', 'width', 'height', 'locked', 'lockOwner', 'x', 'y', 'w', 'h',
  'zIndex', 'rotation', 'scaleX', 'scaleY', 'opacity', 'visible', 'parentId',
  'collapsed', 'variant', 'shapeType', 'diagramType',
]);

const PUBLIC_SCENE_EDGE_KEYS = new Set([
  'id', 'type', 'version', 'from', 'to', 'sourceShapeId', 'targetShapeId',
  'fromAnchor', 'toAnchor', 'fromOffset', 'toOffset', 'fromAnchorLocked',
  'toAnchorLocked', 'sourceAnchor', 'targetAnchor', 'routing', 'route', 'points',
  'label', 'text', 'strokeColor', 'strokeWidth', 'zIndex', 'visible',
]);

const PUBLIC_SCENE_FRAME_KEYS = new Set([
  'id', 'type', 'version', 'name', 'title', 'text', 'x', 'y', 'w', 'h',
  'width', 'height', 'zIndex', 'rotation', 'locked', 'parentId', 'collapsed',
  'fillColor', 'strokeColor', 'strokeWidth', 'visible',
]);

// Process labels, lane headings, shape variants, and visual/routing fields are
// presentation data rather than process metadata. Keep this fixed allowlist
// separate from the public field policy so anonymous viewers cannot receive
// descriptions, ownership, controls, evidence, costs, or arbitrary attrs.
const PROCESS_PUBLIC_NODE_ATTR_KEYS = new Set([
  'label', 'text', 'name', 'title', 'variant', 'shapeType',
  'fill', 'fillColor', 'stroke', 'strokeColor', 'strokeWidth', 'dash', 'dashEnabled',
  'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'textDecoration',
  'textAlign', 'verticalAlign', 'textColor', 'rotation', 'scaleX', 'scaleY',
  'opacity', 'visible', 'locked',
]);

const PROCESS_PUBLIC_EDGE_ATTR_KEYS = new Set([
  'label', 'text', 'routingType', 'routing', 'route', 'points',
  'fromAnchor', 'toAnchor', 'fromOffset', 'toOffset', 'fromAnchorLocked',
  'toAnchorLocked', 'sourceAnchor', 'targetAnchor', 'stroke', 'strokeColor',
  'strokeWidth', 'dash', 'dashEnabled', 'opacity', 'visible',
]);

const PROCESS_PUBLIC_FRAME_ATTR_KEYS = new Set([
  'title', 'name', 'text', 'frameType', 'fill', 'fillColor', 'stroke',
  'strokeColor', 'strokeWidth', 'dash', 'dashEnabled', 'fontFamily', 'fontSize',
  'fontWeight', 'fontStyle', 'textColor', 'textAlign', 'verticalAlign',
  'opacity', 'visible', 'rotation',
]);

// Genogram relation markers are renderer inputs, not arbitrary domain data.
// Keep this list explicit: anonymous snapshots must not regain the old
// pass-through behaviour just to render relationship notation correctly.
const GENOGRAM_PUBLIC_NODE_ATTR_KEYS = new Set([
  'label', 'name', 'sex', 'deceased', 'birthYear', 'deathYear', 'location',
  'occupation', 'fill', 'fillColor', 'stroke', 'strokeColor', 'strokeWidth',
  'dash', 'dashed', 'fontFamily', 'fontSize', 'textColor', 'opacity', 'visible',
  'shapeType', 'variant',
]);

const GENOGRAM_PUBLIC_EDGE_ATTR_KEYS = new Set([
  'label', 'text', 'color', 'width', 'stroke', 'strokeColor', 'strokeWidth',
  'dashed', 'dotted', 'dash', 'dashDot', 'dashDoubleDot', 'dashSingleDot',
  'routingType', 'routing', 'route', 'points', 'opacity', 'visible',
  'doubleLine', 'tripleLine', 'midTicks', 'midCircle', 'wave',
  'adopted', 'guardianship', 'step', 'half', 'separated', 'divorced',
  'engaged', 'nullity', 'legalSeparationSlash', 'separationInFactSlash',
  'startArrow', 'endArrow',
]);

type ViewerScenePresentation = 'default' | 'process' | 'genogram';

const PUBLIC_ARROW_TYPES = new Set([
  'none', 'triangle', 'circle', 'circleOutline', 'diamond', 'bar',
  'outlineBlock', 'backslash', 'chevron', 'triangle-outline',
]);

function pickPublicSceneFields(
  record: Record<string, unknown>,
  allowedKeys: ReadonlySet<string>,
): Record<string, unknown> {
  const picked: Record<string, unknown> = {};
  for (const key of allowedKeys) {
    const value = record[key];
    if (value === undefined || value === null) continue;
    if (typeof value === 'string' || typeof value === 'boolean'
      || (typeof value === 'number' && Number.isFinite(value))) {
      picked[key] = value;
      continue;
    }
    if (key === 'points' && Array.isArray(value)
      && value.every((entry) => typeof entry === 'number' && Number.isFinite(entry))) {
      picked[key] = value.slice(0, 20_000);
    }
  }
  return picked;
}

function sanitizeViewerNode(
  node: unknown,
  policy: ViewerRedactionPolicy,
  publicPresentationKeys?: ReadonlySet<string>,
  presentation: ViewerScenePresentation = 'default',
): Record<string, unknown> | null {
  if (!isObjectRecord(node)) return null;
  const id = truncate(node.id, 120);
  if (!id) return null;
  const sanitized = pickPublicSceneFields(node, PUBLIC_SCENE_NODE_KEYS);
  sanitized.id = id;

  if (isObjectRecord(node.attrs)) {
    sanitized.attrs = presentation === 'genogram'
      ? filterGenogramViewerFieldRecord(node.attrs, policy, publicPresentationKeys ?? new Set())
      : filterViewerFieldRecord(node.attrs, policy, publicPresentationKeys);
  }
  if (isObjectRecord(node.props)) {
    sanitized.props = presentation === 'genogram'
      ? filterGenogramViewerFieldRecord(node.props, policy, publicPresentationKeys ?? new Set())
      : filterViewerFieldRecord(node.props, policy, publicPresentationKeys);
  }

  return sanitized;
}

function sanitizeViewerEdge(
  edge: unknown,
  policy: ViewerRedactionPolicy,
  publicPresentationKeys?: ReadonlySet<string>,
  presentation: ViewerScenePresentation = 'default',
): Record<string, unknown> | null {
  if (!isObjectRecord(edge)) return null;
  const sanitized = pickPublicSceneFields(edge, PUBLIC_SCENE_EDGE_KEYS);

  if (isObjectRecord(edge.attrs)) {
    sanitized.attrs = presentation === 'genogram'
      ? filterGenogramViewerFieldRecord(edge.attrs, policy, publicPresentationKeys ?? new Set())
      : filterViewerFieldRecord(edge.attrs, policy, publicPresentationKeys);
  }
  if (isObjectRecord(edge.props)) {
    sanitized.props = presentation === 'genogram'
      ? filterGenogramViewerFieldRecord(edge.props, policy, publicPresentationKeys ?? new Set())
      : filterViewerFieldRecord(edge.props, policy, publicPresentationKeys);
  }

  return sanitized;
}

function sanitizeViewerFrame(
  frame: unknown,
  policy: ViewerRedactionPolicy,
  publicPresentationKeys?: ReadonlySet<string>,
): Record<string, unknown> | null {
  if (!isObjectRecord(frame)) return null;
  const id = truncate(frame.id, 120);
  if (!id) return null;
  const sanitized = pickPublicSceneFields(frame, PUBLIC_SCENE_FRAME_KEYS);
  sanitized.id = id;
  if (isObjectRecord(frame.attrs)) {
    sanitized.attrs = filterViewerFieldRecord(frame.attrs, policy, publicPresentationKeys);
  }
  if (isObjectRecord(frame.props)) {
    sanitized.props = filterViewerFieldRecord(frame.props, policy, publicPresentationKeys);
  }
  return sanitized;
}

function sanitizeViewerScene(
  scene: ViewerSnapshotPayload['scene'] | undefined,
  policy: ViewerRedactionPolicy,
  recordVisibilityIndex?: RecordVisibilityIndex,
  presentation: ViewerScenePresentation = 'default',
): SanitizedSceneResult {
  const hiddenNodeIds = new Set<string>();
  if (!scene) return { scene: undefined, hiddenNodeIds };
  const nodePresentationKeys = presentation === 'process'
    ? PROCESS_PUBLIC_NODE_ATTR_KEYS
    : presentation === 'genogram' ? GENOGRAM_PUBLIC_NODE_ATTR_KEYS : undefined;
  const edgePresentationKeys = presentation === 'process'
    ? PROCESS_PUBLIC_EDGE_ATTR_KEYS
    : presentation === 'genogram' ? GENOGRAM_PUBLIC_EDGE_ATTR_KEYS : undefined;
  const framePresentationKeys = presentation === 'process' ? PROCESS_PUBLIC_FRAME_ATTR_KEYS : undefined;

  const nodes: unknown[] = [];
  for (const node of scene.nodes) {
    const nodeId = readViewerEntityId(node);
    if (shouldHideViewerEntity(node, policy, recordVisibilityIndex)) {
      if (nodeId) hiddenNodeIds.add(nodeId);
      continue;
    }
    const sanitized = sanitizeViewerNode(node, policy, nodePresentationKeys, presentation);
    if (sanitized) nodes.push(sanitized);
  }

  const edges = scene.edges
    .filter(isPublicViewerEdge)
    .filter((edge) => {
      if (shouldHideViewerEntity(edge, policy, recordVisibilityIndex)) return false;
      const [from, to] = readViewerEdgeEndpoints(edge);
      if (from && hiddenNodeIds.has(from)) return false;
      if (to && hiddenNodeIds.has(to)) return false;
      return true;
    })
    .flatMap((edge) => {
      const sanitized = sanitizeViewerEdge(edge, policy, edgePresentationKeys, presentation);
      return sanitized ? [sanitized] : [];
    });

  const frames = scene.frames?.flatMap((frame) => {
    const sanitized = sanitizeViewerFrame(frame, policy, framePresentationKeys);
    return sanitized ? [sanitized] : [];
  });

  return {
    hiddenNodeIds,
    scene: {
      diagramType: scene.diagramType,
      nodes,
      edges,
      frames: frames && frames.length > 0 ? frames : undefined,
      // Scenario existence and cardinality can themselves be restricted facts,
      // so anonymous snapshots intentionally omit the container.
      // View rules and settings historically accepted arbitrary JSON. They are
      // omitted until their public wire schemas are explicit allowlists.
    },
  };
}

function sanitizeStoredViewerRecord(
  record: ViewerSnapshotRecord,
  domain: ViewerDomain,
  shareId: string,
): ViewerSnapshotRecord {
  const policy = buildViewerRedactionPolicy();
  const payload = isObjectRecord(record.payload)
    ? record.payload as unknown as Record<string, unknown>
    : {};
  const nodes = Array.isArray(payload.nodes) ? payload.nodes : [];
  const edges = Array.isArray(payload.edges) ? payload.edges : [];
  const scene = isObjectRecord(payload.scene) ? payload.scene : undefined;

  const safeSceneResult = scene && Array.isArray(scene.nodes) && Array.isArray(scene.edges)
    ? sanitizeViewerScene({
        diagramType: truncate(scene.diagramType, 40),
        nodes: scene.nodes,
        edges: scene.edges,
        frames: Array.isArray(scene.frames) ? scene.frames : undefined,
        viewRules: scene.viewRules,
        scenarios: Array.isArray(scene.scenarios) ? scene.scenarios : undefined,
        settings: scene.settings,
      }, policy, undefined, domain === 'process'
        ? 'process'
        : domain === 'genogram' ? 'genogram' : 'default')
    : { scene: undefined, hiddenNodeIds: new Set<string>() };
  const hiddenNodeIds = safeSceneResult.hiddenNodeIds;

  const safeNodes: ViewerNode[] = nodes.flatMap((candidate) => {
    if (!isObjectRecord(candidate)) return [];
    const id = truncate(candidate.id, 120);
    if (!id) return [];
    if (hiddenNodeIds.has(id) || shouldHideViewerEntity(candidate, policy)) {
      hiddenNodeIds.add(id);
      return [];
    }
    return [{
      id,
      x: typeof candidate.x === 'number' && Number.isFinite(candidate.x) ? candidate.x : 0,
      y: typeof candidate.y === 'number' && Number.isFinite(candidate.y) ? candidate.y : 0,
      name: truncate(candidate.name, 200),
      title: truncate(candidate.title, 200),
      department: truncate(candidate.department, 200),
      color: truncate(candidate.color, 64),
    }];
  });

  const safeEdges: ViewerEdge[] = edges.flatMap((candidate) => {
    if (!isObjectRecord(candidate) || !isPublicViewerEdge(candidate)) return [];
    const from = truncate(candidate.from, 120);
    const to = truncate(candidate.to, 120);
    if (!from || !to) return [];
    if (hiddenNodeIds.has(from) || hiddenNodeIds.has(to)) return [];
    return [{ from, to, type: truncate(candidate.type, 80) }];
  });

  return {
    domain,
    shareId,
    version: truncate(record.version, 120) ?? generateId(),
    publishedAt: truncate(record.publishedAt, 64) ?? new Date(0).toISOString(),
    payload: {
      domain,
      title: truncate(payload.title, 200),
      sourceDiagramId: truncate(payload.sourceDiagramId, 120),
      fieldVisibility: PUBLIC_VIEWER_FIELD_VISIBILITY,
      nodes: safeNodes,
      edges: safeEdges,
      ...(safeSceneResult.scene ? { scene: safeSceneResult.scene } : {}),
    },
  };
}

function readRecordObject(value: unknown): Record<string, unknown> | null {
  return isObjectRecord(value) ? value : null;
}

function readNestedRecord(value: unknown, key: string): Record<string, unknown> | null {
  const record = readRecordObject(value);
  return record ? readRecordObject(record[key]) : null;
}

function readViewerEntityId(value: unknown): string | null {
  const record = readRecordObject(value);
  const id = record?.id;
  return typeof id === 'string' && id.trim().length > 0 ? id : null;
}

function collectRecordIdentifiersFromObject(
  value: unknown,
  out: Set<string>,
  includeGenericRecordId = false,
): void {
  const record = readRecordObject(value);
  if (!record) return;

  for (const key of ['folderRecordId', 'folder_record_id', 'sourceRecordId', 'source_record_id']) {
    const id = record[key];
    if (typeof id === 'string' && id.trim().length > 0) out.add(id);
  }

  if (includeGenericRecordId) {
    const id = record.recordId;
    if (typeof id === 'string' && id.trim().length > 0) out.add(id);
  }
}

function collectViewerRecordIdentifiers(value: unknown): Set<string> {
  const ids = new Set<string>();
  const record = readRecordObject(value);
  if (!record) return ids;

  collectRecordIdentifiersFromObject(record, ids);
  collectRecordIdentifiersFromObject(record.attrs, ids);
  collectRecordIdentifiersFromObject(record.props, ids);

  const props = readRecordObject(record.props);
  collectRecordIdentifiersFromObject(props?.attrs, ids);

  const propBinding = readRecordObject(props?.dataBinding);
  collectRecordIdentifiersFromObject(propBinding, ids, true);
  collectRecordIdentifiersFromObject(propBinding?.cachedRecord, ids);

  const attrs = readRecordObject(record.attrs);
  const attrBinding = readRecordObject(attrs?.dataBinding);
  collectRecordIdentifiersFromObject(attrBinding, ids, true);
  collectRecordIdentifiersFromObject(attrBinding?.cachedRecord, ids);

  return ids;
}

function readUniversalPropsCandidate(value: unknown): Record<string, unknown> | null {
  const record = readRecordObject(value);
  if (!record) return null;

  const universalProps = readRecordObject(record.universalProps);
  if (universalProps) return universalProps;

  if ('sensitivityTier' in record || 'sensitivity' in record) return record;
  return null;
}

function readEmbeddedRecordUniversalProps(value: unknown): Record<string, unknown> | null {
  const record = readRecordObject(value);
  if (!record) return null;

  const direct = readUniversalPropsCandidate(record);
  if (direct) return direct;

  const attrs = readNestedRecord(record, 'attrs');
  const attrsCandidate = readUniversalPropsCandidate(attrs);
  if (attrsCandidate) return attrsCandidate;

  const props = readNestedRecord(record, 'props');
  const propsCandidate = readUniversalPropsCandidate(props);
  if (propsCandidate) return propsCandidate;

  const propsAttrsCandidate = readUniversalPropsCandidate(props?.attrs);
  if (propsAttrsCandidate) return propsAttrsCandidate;

  const propBinding = readRecordObject(props?.dataBinding);
  const propCachedCandidate = readUniversalPropsCandidate(propBinding?.cachedRecord);
  if (propCachedCandidate) return propCachedCandidate;

  const attrBinding = readRecordObject(attrs?.dataBinding);
  const attrCachedCandidate = readUniversalPropsCandidate(attrBinding?.cachedRecord);
  if (attrCachedCandidate) return attrCachedCandidate;

  return null;
}

function shouldHideViewerEntity(
  value: unknown,
  policy: ViewerRedactionPolicy,
  recordVisibilityIndex?: RecordVisibilityIndex,
): boolean {
  const recordIds = collectViewerRecordIdentifiers(value);
  for (const id of recordIds) {
    if (recordVisibilityIndex?.hiddenRecordIds.has(id)) return true;
  }

  const hasKnownVisibleRecord = [...recordIds].some((id) => recordVisibilityIndex?.knownRecordIds.has(id));
  if (hasKnownVisibleRecord) return false;

  const universalProps = readEmbeddedRecordUniversalProps(value);
  if (universalProps) {
    return evaluateRecordVisibility(
      { universalProps },
      { viewerTierCeiling: policy.recordTierCeiling },
    ).disposition === 'hidden';
  }

  if (recordIds.size > 0) {
    return evaluateRecordVisibility(
      { universalProps: {} },
      { viewerTierCeiling: policy.recordTierCeiling },
    ).disposition === 'hidden';
  }

  return false;
}

function readViewerEdgeEndpoints(edge: unknown): [string | null, string | null] {
  const record = readRecordObject(edge);
  if (!record) return [null, null];
  const attrs = readRecordObject(record.attrs);
  const props = readRecordObject(record.props);

  const from = firstString(
    record.sourceShapeId,
    record.source,
    record.fromId,
    record.from,
    attrs?.sourceShapeId,
    attrs?.source,
    attrs?.fromId,
    attrs?.from,
    props?.sourceShapeId,
    props?.source,
    props?.fromId,
    props?.from,
  );
  const to = firstString(
    record.targetShapeId,
    record.target,
    record.toId,
    record.to,
    attrs?.targetShapeId,
    attrs?.target,
    attrs?.toId,
    attrs?.to,
    props?.targetShapeId,
    props?.target,
    props?.toId,
    props?.to,
  );
  return [from, to];
}

function firstString(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === 'string' && value.trim().length > 0) return value;
  }
  return null;
}

function filterTopLevelViewerNodes(
  nodes: Array<z.infer<typeof OrgChartPublishSchema>['nodes'][number]>,
  hiddenNodeIds: Set<string>,
): ViewerNode[] {
  return nodes
    .filter((node) => !hiddenNodeIds.has(node.id))
    .map((node): ViewerNode => ({
      id: node.id,
      x: Number.isFinite(node.x) ? node.x : 0,
      y: Number.isFinite(node.y) ? node.y : 0,
      name: node.name,
      title: node.title,
      department: node.department,
      color: node.color,
    }));
}

function filterTopLevelViewerEdges(
  edges: Array<z.infer<typeof OrgChartPublishSchema>['edges'][number]>,
  hiddenNodeIds: Set<string>,
): ViewerEdge[] {
  return edges
    .filter((edge) =>
      isPublicViewerEdge(edge)
      && !hiddenNodeIds.has(edge.from)
      && !hiddenNodeIds.has(edge.to)
    )
    .map((edge): ViewerEdge => ({
      from: edge.from,
      to: edge.to,
      type: edge.type,
    }));
}

async function buildRecordVisibilityIndex(
  listRecords: () => Promise<Array<{ id: string; externalId: string | null; universalProps: Record<string, unknown> }>>,
  viewerTierCeiling: ViewerTierCeiling,
): Promise<RecordVisibilityIndex> {
  const hiddenRecordIds = new Set<string>();
  const knownRecordIds = new Set<string>();
  const records = await listRecords();

  for (const record of records) {
    const decision = evaluateRecordVisibility(record, { viewerTierCeiling });
    addRecordIdentifier(knownRecordIds, record.id);
    addRecordIdentifier(knownRecordIds, record.externalId);
    if (decision.disposition === 'hidden') {
      addRecordIdentifier(hiddenRecordIds, record.id);
      addRecordIdentifier(hiddenRecordIds, record.externalId);
    }
  }

  return { hiddenRecordIds, knownRecordIds };
}

function addRecordIdentifier(target: Set<string>, value: string | null | undefined): void {
  if (typeof value === 'string' && value.trim().length > 0) target.add(value);
}

export async function loadStoredDiagramSnapshot(
  diagram: StoredDiagramSnapshot,
  loadR2Snapshot?: R2SnapshotLoader,
): Promise<Uint8Array> {
  if (diagram.storageProvider === 'r2' && diagram.snapshotKey) {
    if (!loadR2Snapshot) {
      throw new PublishFromDiagramError('Diagram snapshot storage unavailable', 503);
    }
    try {
      return await loadR2Snapshot(diagram.snapshotKey);
    } catch {
      throw new PublishFromDiagramError('Failed to load diagram snapshot from storage', 502);
    }
  }

  if (diagram.ydocSnapshot) {
    return base64ToUint8Array(diagram.ydocSnapshot);
  }

  throw new PublishFromDiagramError('Diagram snapshot not available', 409);
}

export class ViewerSnapshotService {
  constructor(
    private readonly repository: IViewerSnapshotRepository,
    private readonly dependencies: ViewerSnapshotServiceDependencies = {},
  ) {}

  async getLatest(domain: ViewerDomain, shareId: string): Promise<ViewerSnapshotRecord | null> {
    const stored = await this.repository.getLatest(domain, shareId);
    if (!stored) return null;

    // KV snapshots can outlive the publisher version that created them. Treat
    // persisted snapshots as untrusted input and re-apply the current anonymous
    // viewer contract on every read so a missed regeneration cohort cannot
    // expose protected fields, relation existence, scenarios, or binding caches.
    return sanitizeStoredViewerRecord(stored, domain, shareId);
  }

  async validatePublicReadAccess(params: ValidatePublicReadAccessParams): Promise<void> {
    const { shareId, db } = params;
    const { diagramAccessLinks } = await import('../../../infrastructure/database/schema');
    const { eq } = await import('drizzle-orm');
    const typedDb = db as ReturnType<typeof import('drizzle-orm/postgres-js').drizzle>;
    const [accessLink] = await typedDb
      .select({
        revokedAt: diagramAccessLinks.revokedAt,
        expiresAt: diagramAccessLinks.expiresAt,
      })
      .from(diagramAccessLinks)
      .where(eq(diagramAccessLinks.token, shareId));

    if (!accessLink) throw new PublishFromDiagramError('Share link not found', 404);
    if (accessLink.revokedAt) throw new PublishFromDiagramError('Share link revoked', 410);
    if (accessLink.expiresAt && new Date(accessLink.expiresAt).getTime() <= Date.now()) {
      throw new PublishFromDiagramError('Share link expired', 410);
    }
  }

  async validatePublishAccess(params: ValidatePublishAccessParams): Promise<void> {
    const { shareId, diagramId, db, auth } = params;

    if (!auth.userId) throw new PublishFromDiagramError('Authentication required', 401);

    const { diagrams, diagramAccessLinks } = await import('../../../infrastructure/database/schema');
    const { and, eq } = await import('drizzle-orm');
    const { DrizzleDiagramShareRepository } = await import('../../../infrastructure/database/repositories/DrizzleDiagramShareRepository');
    const { DrizzleFolderShareRepository } = await import('../../../infrastructure/database/repositories/DrizzleFolderShareRepository');

    const typedDb = db as ReturnType<typeof import('drizzle-orm/postgres-js').drizzle>;
    const [diagram] = await typedDb.select().from(diagrams).where(eq(diagrams.id, diagramId));
    if (!diagram) throw new PublishFromDiagramError('Diagram not found', 404);
    const supportedTypes = new Set(['org', 'flowchart', 'bpmn', 'genogram']);
    if (!supportedTypes.has(diagram.type)) throw new PublishFromDiagramError(`Unsupported diagram type: ${diagram.type}`, 400);

    const [accessLink] = await typedDb
      .select({ token: diagramAccessLinks.token })
      .from(diagramAccessLinks)
      .where(and(eq(diagramAccessLinks.diagramId, diagramId), eq(diagramAccessLinks.token, shareId)));
    if (!accessLink) throw new PublishFromDiagramError('Share link not found for diagram', 404);

    const diagramShareRepo = new DrizzleDiagramShareRepository(db);
    const folderShareRepo = new DrizzleFolderShareRepository(db);
    const ac = new AccessControlService({
      getDiagramUserShareRole: (dId, uId) => diagramShareRepo.getUserShareRole(dId, uId),
      getDiagramAccessSettings: (dId, orgId) => diagramShareRepo.getAccessSettings(dId, orgId),
      getDiagramGuestShareRole: (dId, uId) => diagramShareRepo.getGuestShareRole(dId, uId),
      getFolderUserShareRole: (fId, uId) => folderShareRepo.getUserShareRole(fId, uId),
      getFolderGuestShareRole: (fId, uId) => folderShareRepo.getGuestShareRole(fId, uId),
      getFolderAccessSettings: (fId, orgId) => folderShareRepo.getAccessSettings(fId, orgId),
    }, auth.orgId);
    const role = await ac.effectiveDiagramRole({
      id: diagram.id,
      orgId: String(diagram.orgId),
      ownerUserId: diagram.ownerUserId || null,
      folderId: diagram.folderId,
    }, auth.userId);
    if (!ac.canWrite(role as RoleLevel)) throw new PublishFromDiagramError('Forbidden', 403);
  }

  async publishOrgChart(shareId: string, input: unknown, domain: ViewerDomain = 'orgchart'): Promise<ViewerSnapshotRecord> {
    const parsed = OrgChartPublishSchema.parse(input);
    const fieldVisibility: FieldVisibilityPayload = PUBLIC_VIEWER_FIELD_VISIBILITY;
    const redactionPolicy = buildViewerRedactionPolicy();
    const sanitizedScene = sanitizeViewerScene(parsed.scene ? {
      diagramType: parsed.scene.diagramType,
      nodes: parsed.scene.nodes,
      edges: parsed.scene.edges,
      frames: parsed.scene.frames,
      viewRules: parsed.scene.viewRules,
      scenarios: parsed.scene.scenarios,
      settings: parsed.scene.settings,
    } : undefined, redactionPolicy, undefined, domain === 'genogram' ? 'genogram' : 'default');

    const payload: ViewerSnapshotPayload = {
      domain,
      title: parsed.title,
      sourceDiagramId: parsed.id,
      fieldVisibility,
      nodes: filterTopLevelViewerNodes(parsed.nodes, sanitizedScene.hiddenNodeIds),
      edges: filterTopLevelViewerEdges(parsed.edges, sanitizedScene.hiddenNodeIds),
      scene: sanitizedScene.scene,
    };

    const record: ViewerSnapshotRecord = {
      domain,
      shareId,
      version: generateId(),
      publishedAt: new Date().toISOString(),
      payload,
    };
    await this.repository.publish(record);
    return record;
  }

  async publishFromDiagram(params: PublishFromDiagramParams): Promise<PublishFromDiagramResult> {
    const { shareId, diagramId, db, auth } = params;
    const fieldVisibilityConfig = PUBLIC_VIEWER_FIELD_VISIBILITY;
    const redactionPolicy = buildViewerRedactionPolicy();

    const { diagrams, diagramAccessLinks, diagramUpdates } = await import('../../../infrastructure/database/schema');
    const { and, eq, gt } = await import('drizzle-orm');
    const { DrizzleDiagramShareRepository } = await import('../../../infrastructure/database/repositories/DrizzleDiagramShareRepository');
    const { DrizzleFolderShareRepository } = await import('../../../infrastructure/database/repositories/DrizzleFolderShareRepository');
    const { DrizzleFolderRecordRepository } = await import('../../../infrastructure/database/repositories/DrizzleFolderRecordRepository');

    // Cast once — the db instance is an untyped Drizzle client (see initRequestDb)
    const typedDb = db as ReturnType<typeof import('drizzle-orm/postgres-js').drizzle>;
    const [diagram] = await typedDb.select().from(diagrams).where(eq(diagrams.id, diagramId));
    if (!diagram) throw new PublishFromDiagramError('Diagram not found', 404);
    const publishSupportedTypes = new Set(['org', 'flowchart', 'bpmn', 'genogram']);
    if (!publishSupportedTypes.has(diagram.type)) throw new PublishFromDiagramError(`Unsupported diagram type: ${diagram.type}`, 400);
    const isProcess = diagram.type === 'flowchart' || diagram.type === 'bpmn';
    const isGenogram = diagram.type === 'genogram';
    const viewerDomain: ViewerDomain = isProcess ? 'process' : isGenogram ? 'genogram' : 'orgchart';

    const [accessLink] = await typedDb
      .select({
        token: diagramAccessLinks.token,
        role: diagramAccessLinks.role,
        revokedAt: diagramAccessLinks.revokedAt,
        expiresAt: diagramAccessLinks.expiresAt,
      })
      .from(diagramAccessLinks)
      .where(and(eq(diagramAccessLinks.diagramId, diagramId), eq(diagramAccessLinks.token, shareId)));
    if (!accessLink) throw new PublishFromDiagramError('Share link not found for diagram', 404);
    if (accessLink.revokedAt) throw new PublishFromDiagramError('Share link revoked', 409);
    if (accessLink.expiresAt && new Date(accessLink.expiresAt).getTime() < Date.now()) {
      throw new PublishFromDiagramError('Share link expired', 409);
    }

    const userId = auth.userId || null;
    const userOrgId = auth.orgId;
    const diagramShareRepo = new DrizzleDiagramShareRepository(db);
    const folderShareRepo = new DrizzleFolderShareRepository(db);
    const ac = new AccessControlService({
      getDiagramUserShareRole: (dId, uId) => diagramShareRepo.getUserShareRole(dId, uId),
      getDiagramAccessSettings: (dId, orgId) => diagramShareRepo.getAccessSettings(dId, orgId),
      getDiagramGuestShareRole: (dId, uId) => diagramShareRepo.getGuestShareRole(dId, uId),
      getFolderUserShareRole: (fId, uId) => folderShareRepo.getUserShareRole(fId, uId),
      getFolderGuestShareRole: (fId, uId) => folderShareRepo.getGuestShareRole(fId, uId),
      getFolderAccessSettings: (fId, orgId) => folderShareRepo.getAccessSettings(fId, orgId),
    }, userOrgId);
    const role = await ac.effectiveDiagramRole({
      id: diagram.id,
      orgId: String(diagram.orgId),
      ownerUserId: diagram.ownerUserId || null,
      folderId: diagram.folderId,
    }, userId);
    if (!ac.canWrite(role as RoleLevel)) throw new PublishFromDiagramError('Forbidden', 403);

    const doc = new Y.Doc();
    const snapshotBytes = await loadStoredDiagramSnapshot(
      diagram,
      this.dependencies.loadR2Snapshot,
    );
    Y.applyUpdate(doc, snapshotBytes);

    const pendingUpdates = await typedDb
      .select({ seq: diagramUpdates.seq, updateBytes: diagramUpdates.updateBytes })
      .from(diagramUpdates)
      .where(and(
        eq(diagramUpdates.diagramId, diagramId),
        gt(diagramUpdates.seq, diagram.snapshotSeq ?? 0),
      ))
      .orderBy(diagramUpdates.seq);

    for (const update of pendingUpdates) {
      Y.applyUpdate(doc, base64ToUint8Array(update.updateBytes));
    }

    // Extract shapes matching the domain — avoids building
    // a full scene array with ALL nodes (the biggest memory hog).
    const allowedTypes = isProcess ? PROCESS_NODE_TYPES
      : isGenogram ? GENOGRAM_NODE_TYPES
      : ORG_NODE_TYPES;
    const shapes = doc.getMap<unknown>('shapes');
    const connectors = doc.getMap<unknown>('connectors');
    const boundRecordStateMap = doc.getMap('boundRecordState');
    const folderRecordRepo = new DrizzleFolderRecordRepository(
      db as ConstructorParameters<typeof DrizzleFolderRecordRepository>[0],
    );
    const recordVisibilityIndex = await buildRecordVisibilityIndex(
      () => folderRecordRepo.listByFolder(diagram.folderId),
      redactionPolicy.recordTierCeiling,
    );

    const extractedNodes: ViewerNode[] = [];
    const extractedNodeIds = new Set<string>();
    shapes.forEach((raw, id) => {
      if (raw == null) return;
      const shape = readYjsValue<Record<string, unknown>>(raw);
      if (!shape || typeof shape !== 'object') return;
      const nodeType = (shape.type as string) ?? 'shape';
      if (!allowedTypes.has(nodeType)) return;
      if (shouldHideViewerEntity({ ...shape, id }, redactionPolicy, recordVisibilityIndex)) return;

      // Enrich with bound record data
      const props = (typeof shape.props === 'object' && shape.props !== null)
        ? shape.props as Record<string, unknown>
        : undefined;
      const attrs = (typeof shape.attrs === 'object' && shape.attrs !== null)
        ? shape.attrs as Record<string, unknown>
        : undefined;

      let enrichedAttrs = attrs ?? {};
      const binding = props?.dataBinding as {
        sourceId?: string;
        recordId?: string;
        cachedRecord?: Record<string, unknown>;
        localChanges?: { fields?: Record<string, unknown> };
      } | undefined;

      if (binding?.cachedRecord) {
        const merged: Record<string, unknown> = { ...binding.cachedRecord };
        if (binding.localChanges?.fields) {
          Object.assign(merged, binding.localChanges.fields);
        }
        if (binding.sourceId && binding.recordId) {
          const brKey = `${binding.sourceId}:${binding.recordId}`;
          const brState = boundRecordStateMap.get(brKey) as
            | { localChanges?: Record<string, unknown> }
            | undefined;
          if (brState?.localChanges) {
            Object.assign(merged, brState.localChanges);
          }
        }
        enrichedAttrs = { ...merged, ...enrichedAttrs };
      }

      const visibleAttrs = filterViewerFieldRecord(enrichedAttrs, redactionPolicy);
      const visibleProps = props ? filterViewerFieldRecord(props, redactionPolicy) : undefined;
      const x = typeof shape.x === 'number' && Number.isFinite(shape.x) ? shape.x : 0;
      const y = typeof shape.y === 'number' && Number.isFinite(shape.y) ? shape.y : 0;

      extractedNodes.push({
        id,
        x,
        y,
        name: pickRecordValue(visibleAttrs, ['name', 'label']) ?? pickRecordValue(visibleProps, ['name', 'label']) ?? truncate(shape.name, 200) ?? truncate(shape.text, 200),
        title: pickRecordValue(visibleAttrs, ['title', 'role']) ?? pickRecordValue(visibleProps, ['title', 'role']),
        department: pickRecordValue(visibleAttrs, ['department']) ?? pickRecordValue(visibleProps, ['department']),
        color: pickRecordValue(visibleAttrs, ['color', 'fill']) ?? pickRecordValue(visibleProps, ['color', 'fill']),
      });
      extractedNodeIds.add(id);
    });

    // Extract only edges that connect org nodes
    const extractedEdges: ViewerEdge[] = [];
    connectors.forEach((raw) => {
      if (!raw || typeof raw !== 'object') return;
      const conn = raw as Record<string, unknown>;
      const from = (conn.sourceShapeId ?? conn.source ?? conn.fromId ?? conn.from) as string | undefined;
      const to = (conn.targetShapeId ?? conn.target ?? conn.toId ?? conn.to) as string | undefined;
      if (!from || !to || typeof from !== 'string' || typeof to !== 'string') return;
      if (!extractedNodeIds.has(from) || !extractedNodeIds.has(to)) return;
      if (!isPublicViewerEdge(conn)) return;

      const connAttrs = (typeof conn.attrs === 'object' && conn.attrs !== null)
        ? conn.attrs as Record<string, unknown>
        : undefined;
      extractedEdges.push({
        from,
        to,
        type: truncate(conn.type, 80) ?? pickRecordValue(connAttrs, ['subtype', 'type']),
      });
    });

    // Process diagrams carry structure the flattened {id,x,y,name} node model
    // cannot express — swimlane pool tables, shape variants (process/decision/
    // start-end), edge routing, and the rich attrs the viewer lenses color by.
    // Publish the full scene (same proven path as org/genogram) so the viewer
    // renders identically to the editor instead of falling back to the lossy
    // legacy normalizer. Must run before doc.destroy().
    let scene: ViewerSnapshotPayload['scene'] | undefined;
    if (isProcess) {
      const fullScene = extractSceneFromYDoc(doc);
      if (fullScene.nodes.length <= MAX_SCENE_NODES) {
        const framesMap = doc.getMap('frames');
        const frames: unknown[] = [];
        framesMap.forEach((record) => frames.push(record));

        const viewRulesMap = doc.getMap('viewRules');
        const viewRulesData: Record<string, unknown> = {};
        for (const key of ['rules', 'customPresets', 'presets']) {
          const val = viewRulesMap.get(key);
          if (typeof val === 'string') {
            try { viewRulesData[key] = JSON.parse(val); } catch { /* skip malformed */ }
          }
        }

        scene = sanitizeViewerScene({
          diagramType: diagram.type,
          nodes: fullScene.nodes,
          edges: fullScene.edges,
          frames: frames.length > 0
            ? frames
            : undefined,
          viewRules: Object.keys(viewRulesData).length > 0
            ? viewRulesData
            : undefined,
        }, redactionPolicy, recordVisibilityIndex, 'process').scene;
      }
    }

    // Release YDoc memory immediately
    doc.destroy();

    const record = await this.publishDomain(viewerDomain, shareId, {
      title: truncate(diagram.name, 200),
      sourceDiagramId: diagramId,
      nodes: extractedNodes,
      edges: extractedEdges,
      fieldVisibility: fieldVisibilityConfig,
      ...(scene ? { scene } : {}),
    });
    return {
      version: record.version,
      publishedAt: record.publishedAt,
      nodeCount: extractedNodes.length,
      edgeCount: extractedEdges.length,
    };
  }

  async publishDomain(domain: ViewerDomain, shareId: string, input: unknown): Promise<ViewerSnapshotRecord> {
    if (!EMBEDDABLE_VIEWER_DOMAINS.has(domain)) {
      throw new PublishFromDiagramError(`Unsupported viewer domain: ${domain}`, 400);
    }
    const embeddableDomain = domain as Exclude<ViewerDomain, 'network'>;

    const parsed = OrgChartPublishSchema.parse(input);
    const diagramTypeMap: Record<Exclude<ViewerDomain, 'network'>, string> = {
      orgchart: 'org',
      process: parsed.scene?.diagramType ?? 'flowchart',
      genogram: 'genogram',
    };

    const fieldVisibility: FieldVisibilityPayload = PUBLIC_VIEWER_FIELD_VISIBILITY;
    const redactionPolicy = buildViewerRedactionPolicy();
    const sanitizedScene = sanitizeViewerScene(parsed.scene ? {
      diagramType: diagramTypeMap[embeddableDomain],
      nodes: parsed.scene.nodes,
      edges: parsed.scene.edges,
      frames: parsed.scene.frames,
      viewRules: parsed.scene.viewRules,
      scenarios: parsed.scene.scenarios,
      settings: parsed.scene.settings,
    } : undefined, redactionPolicy, undefined, embeddableDomain === 'process'
      ? 'process'
      : embeddableDomain === 'genogram' ? 'genogram' : 'default');

    const payload: ViewerSnapshotPayload = {
      domain: embeddableDomain,
      title: parsed.title,
      sourceDiagramId: parsed.sourceDiagramId,
      fieldVisibility,
      nodes: filterTopLevelViewerNodes(parsed.nodes, sanitizedScene.hiddenNodeIds),
      edges: filterTopLevelViewerEdges(parsed.edges, sanitizedScene.hiddenNodeIds),
      scene: sanitizedScene.scene,
    };

    const record: ViewerSnapshotRecord = {
      domain,
      shareId,
      version: generateId(),
      publishedAt: new Date().toISOString(),
      payload,
    };
    await this.repository.publish(record);
    return record;
  }

  async publishProcess(shareId: string, input: unknown): Promise<ViewerSnapshotRecord> {
    const parsed = OrgChartPublishSchema.parse(input);
    const fieldVisibility: FieldVisibilityPayload = PUBLIC_VIEWER_FIELD_VISIBILITY;
    const redactionPolicy = buildViewerRedactionPolicy();
    const sanitizedScene = sanitizeViewerScene(parsed.scene ? {
      diagramType: parsed.scene.diagramType ?? 'flowchart',
      nodes: parsed.scene.nodes,
      edges: parsed.scene.edges,
      frames: parsed.scene.frames,
      viewRules: parsed.scene.viewRules,
      scenarios: parsed.scene.scenarios,
      settings: parsed.scene.settings,
    } : undefined, redactionPolicy, undefined, 'process');
    const payload: ViewerSnapshotPayload = {
      domain: 'process',
      title: parsed.title,
      fieldVisibility,
      nodes: filterTopLevelViewerNodes(parsed.nodes, sanitizedScene.hiddenNodeIds),
      edges: filterTopLevelViewerEdges(parsed.edges, sanitizedScene.hiddenNodeIds),
      scene: sanitizedScene.scene,
    };

    const record: ViewerSnapshotRecord = {
      domain: 'process',
      shareId,
      version: generateId(),
      publishedAt: new Date().toISOString(),
      payload,
    };
    await this.repository.publish(record);
    return record;
  }
}
